import { useState, useEffect } from 'react';
import { Modal } from '../../../components/base/Modal';
import { Button } from '../../../components/base/Button';
import { Input } from '../../../components/base/Input';
import TagInput from '../../../components/base/TagInput';
import RichTextEditor from '../../../components/base/RichTextEditor';
import ImageUpload from '../../../components/base/ImageUpload';
import PostPreview from '../../../components/base/PostPreview';
import { useAuth } from '../../../hooks/useAuth';
import { postsApi, activitiesApi } from '../../../lib/api';
import { useReputationRewards } from '../../../hooks/useReputationRewards';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCategory?: string;
  categories: Array<{ id: string; name: string; color?: string; icon?: string }>;
  onPostCreated: () => void;
}

export default function CreatePostModal({
  isOpen,
  onClose,
  defaultCategory = '',
  categories,
  onPostCreated
}: CreatePostModalProps) {
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [step, setStep] = useState<'edit' | 'preview'>('edit');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    content_html: '',
    category_id: '',
    tags: [] as string[],
    images: [] as string[]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Set default category when modal opens
  useEffect(() => {
    if (isOpen && categories.length > 0) {
      let categoryId = '';
      
      if (defaultCategory) {
        // Map category names to find the correct one
        const categoryNameMap: { [key: string]: string } = {
          'creators': 'Creator Spotlight',
          'general': 'General Discussion',
          'reviews': 'Drama Reviews',
          'recommendations': 'Recommendations',
          'news': 'Industry News',
          'behind-scenes': 'Behind the Scenes',
          'ai-dramas': 'AI Dramas'
        };
        
        const targetCategoryName = categoryNameMap[defaultCategory] || defaultCategory;
        const foundCategory = categories.find(cat => 
          cat.name === targetCategoryName ||
          cat.name.toLowerCase().includes(targetCategoryName.toLowerCase())
        );
        categoryId = foundCategory ? foundCategory.id : categories[0].id;
      } else {
        categoryId = categories[0].id;
      }
      
      setFormData(prev => ({ ...prev, category_id: categoryId }));
    }
  }, [isOpen, defaultCategory, categories]);

  // Reset step when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('edit');
      setError('');
    }
  }, [isOpen]);

  const handlePublish = async () => {
    if (!user) {
      setError('You must be logged in to create a post');
      return;
    }

    if (!formData.title.trim() || !formData.content.trim() || !formData.category_id) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create the post via API
      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        content_html: formData.content_html || formData.content.trim(),
        category_id: formData.category_id,
        author_id: user.id,
        tags: formData.tags.length > 0 ? formData.tags : null,
        images: formData.images.length > 0 ? formData.images : null,
        status: 'pending',
        upvotes: 0,
        downvotes: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const response = await postsApi.createPost(postData);

      if (!response.success) {
        throw new Error(response.error || 'Failed to create post');
      }

      // Award reputation points for creating a post
      const rewardResult = await awardPoints(user.id, 'create_post');
      const pointsEarned = rewardResult.success ? rewardResult.pointsAwarded : 0;

      // Create activity record via API (non-blocking)
      activitiesApi.createActivity({
        activityType: 'post_created',
        title: 'Created a new post',
        description: formData.title,
        data: {
          post_id: response.data?.id,
          category_id: formData.category_id
        }
      }).catch(err => console.warn('Activity creation failed:', err));

      // Reset form
      setFormData({
        title: '',
        content: '',
        content_html: '',
        category_id: categories.length > 0 ? categories[0].id : '',
        tags: [],
        images: []
      });
      setStep('edit');

      onPostCreated();
      onClose();
      
      // Show success notification with points earned
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 transition-all duration-300';
      notification.innerHTML = `
        <div class="flex items-center gap-3">
          <i class="ri-check-circle-line text-2xl"></i>
          <div>
            <p class="font-semibold">Post submitted successfully!</p>
            <p class="text-sm text-white/90">Your post is pending review. ${pointsEarned > 0 ? `You earned +${pointsEarned} reputation points!` : ''}</p>
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.classList.add('opacity-0', 'translate-x-full');
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 300);
      }, 4000);

    } catch (err: any) {
      console.error('Error creating post:', err);
      setError(err.message || 'Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploaded = (url: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, url]
    }));
  };

  const handleImageRemoved = (url: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== url)
    }));
  };

  const handleContentChange = (content: string, html?: string) => {
    setFormData(prev => ({
      ...prev,
      content,
      content_html: html || content
    }));
  };

  const handleReset = () => {
    setFormData({
      title: '',
      content: '',
      content_html: '',
      category_id: categories.length > 0 ? categories[0].id : '',
      tags: [],
      images: []
    });
    setError('');
    setStep('edit');
  };

  const handlePreview = () => {
    if (!formData.title.trim() || !formData.content.trim() || !formData.category_id) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setStep('preview');
  };

  const selectedCategory = categories.find(cat => cat.id === formData.category_id);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={step === 'edit' ? 'Create New Post' : 'Preview Post'} size="large">
      <div className="max-w-2xl mx-auto">
        {step === 'edit' ? (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer"
                required
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter an engaging title for your post..."
                maxLength={255}
                required
                className="w-full"
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.title.length}/255 characters
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Share your thoughts, insights, or questions with the community..."
                className="w-full"
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.content.length}/5000 characters
              </p>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (Optional)
              </label>
              <TagInput
                tags={formData.tags}
                onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                placeholder="Add relevant tags to help others find your post..."
                maxTags={8}
              />
              <p className="text-sm text-gray-500 mt-1">
                Add up to 8 tags to categorize your post
              </p>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={formData.images}
                className="mb-3"
              />
              <p className="text-sm text-gray-500 mt-1">
                Upload up to 5 images to enhance your post (max 2MB each)
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleReset();
                  onClose();
                }}
                className="whitespace-nowrap cursor-pointer"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!formData.title.trim() || !formData.content.trim() || !formData.category_id}
                className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line mr-2"></i>
                Preview Post
              </Button>
            </div>
          </form>
        ) : (
          <PostPreview
            title={formData.title}
            content={formData.content}
            contentHtml={formData.content_html}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'Anonymous',
              avatar: user?.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.id}`
            }}
            category={selectedCategory?.name || 'General'}
            categoryColor={selectedCategory?.color || 'bg-purple-600'}
            categoryIcon={selectedCategory?.icon || 'ri-discuss-line'}
            tags={formData.tags}
            images={formData.images}
            onBack={() => setStep('edit')}
            onSubmit={handlePublish}
            loading={loading}
            submitButtonText="Publish Post"
          />
        )}
      </div>
    </Modal>
  );
}
